import { format }           from 'date-fns';
import { isSameDay }        from 'date-fns';
import { parseISO }         from 'date-fns';
import { useTwilioContext } from './wait_for_twilio';
import InitialsAvatar       from '../components/initials_avatar';
import React                from 'react';
import Spinner              from '../components/spinner';
import useNewMessages       from './use_new_messages';
import useServerMessages    from './use_server_messages';


export default function MessagesList({ conversation, userID }) {
  const listRef        = React.useRef();
  const serverMessages = useServerMessages(conversation);
  const newMessages    = useNewMessages({ conversation });
  const allMessages    = getAllMessages({ serverMessages, newMessages });

  function scrollListToBottom() {
    const list = listRef.current;
    if (!list)
      return;

    list.scrollTop = list.scrollHeight;
  }

  React.useEffect(function() {
    scrollListToBottom();
  }, [ allMessages ]);

  return (
    <div className="conversation-view__messages-list" ref={listRef}>
      <SequenceByDay
        messages={allMessages}
        userID={userID}
        scrollListToBottom={scrollListToBottom}
      />
    </div>
  );
}

function SequenceByDay({ messages, userID, scrollListToBottom }) {
  const sequences = sequenceBy(messages, byDay());
  return sequences
    .map((inSequence, i) => (
      <TimeSequence
        key={i}
        timestamp={inSequence[0].dateCreated}
        messages={inSequence}
        userID={userID}
        scrollListToBottom={scrollListToBottom}
      />
    ));
}

// Render a squence of messages that occurred in the same timeframe
function TimeSequence({ timestamp, messages, userID, scrollListToBottom }) {
  return (
    <div>
      <MessagesGroupHeader timestamp={timestamp}/>
      <List
        messages={messages}
        userID={userID}
        scrollListToBottom={scrollListToBottom}
      />
    </div>
  );
}

function MessagesGroupHeader({ timestamp }) {
  const parsedTime = typeof (timestamp) === 'string' ? parseISO(timestamp) : timestamp;

  return (
    <div className="group-header">
      {format(parsedTime, 'MMMM dd, yyyy')}
    </div>
  );
}

function List({ messages, userID, scrollListToBottom }) {
  return messages.map(message => {
    return <MessageBubble
      key={message.sid}
      userID={userID}
      message={message}
      scrollListToBottom={scrollListToBottom}
    />;
  });
}

function MessageBubble({ userID, message, scrollListToBottom }) {
  const { author }        = message;
  const { body }          = message;
  const { media }         = message;
  const { dateCreated }   = message;
  const isMessageFromUser = author.includes(userID);
  const className         = isMessageFromUser ? 'message-sent' : 'message-received';

  return (
    <div className={className}>
      <div className="message-content">
        <Avatar identity={author}/>
        <div className="message-bubble-and-status">
          <div className="message-bubble">
            {media && <MediaMessage media={media} scrollListToBottom={scrollListToBottom}/>}
            {body && <TextMessage body={body}/>}
            <div className="message-timestamp">{format(dateCreated, 'h:mm a')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Avatar({ identity }) {
  const [ userName, setUserName ] = React.useState('');
  const { client }                = useTwilioContext();

  React.useEffect(function() {
    async function getUserName() {
      const { friendlyName } = await client.getUserDescriptor(identity);
      setUserName(friendlyName);
    }

    getUserName();
  }, [ client, identity ]);

  return <InitialsAvatar name={userName}/>;
}

function MediaMessage({ media, scrollListToBottom }) {
  const [ contentURL, setContentURL ] = React.useState(null);
  const [ isLoaded, setIsLoaded ]     = React.useState(false);

  React.useEffect(function() {
    async function getContentURL() {
      const url = await media.getContentUrl();
      setContentURL(url);
    }

    getContentURL();
  }, [ media, scrollListToBottom ]);

  React.useEffect(function() {
    if (isLoaded)
      scrollListToBottom();
  }, [ isLoaded, scrollListToBottom ]);

  function onLoaded() {
    setIsLoaded(true);
  }

  if (!contentURL)
    return <MediaSpinner/>;

  return (
    <React.Fragment>
      <img
        style={{ display: isLoaded ? 'inherit' : 'none' }}
        src={contentURL}
        onLoad={onLoaded}
      />
      <MediaSpinner show={!isLoaded}/>
    </React.Fragment>
  );
}

function TextMessage({ body }) {
  return (
    <div className="message-text">
      {body}
    </div>
  );
}

function MediaSpinner({ show = true }) {
  const display = show ? 'inherit' : 'none';

  return (
    <div className="spinner-wrapper" style={{ display }}>
      <Spinner size={40}/>
    </div>
  );
}

function getAllMessages({ serverMessages, newMessages }) {
  const allMessages = serverMessages.concat(newMessages);
  const unique      = getUniqueListBy(allMessages, 'sid');
  return unique;
}

function getUniqueListBy(arr, key) {
  return [ ...new Map(arr.map(item => [ item[key], item ])).values() ];
}

function byDay() {
  return function(previous, current) {
    return isSameDay(current.dateCreated, previous.dateCreated);
  };
}

// Given a collection of items, returns a collection of collections, each a
// consecutive sequence of elements, as determined by the predicate.  Retains
// the order of the source collection.
//
// For example:
//  sequenceBy([1, 1, 1, 2, 5, 5], (a, b)=> a === b)
//  => [ [1, 1, 1], [2], [5, 5] ]
//
//  sequences = sequenceBy(original, predicate);
//  assert.deepEqual( flatten(sequences), original);
function sequenceBy(collection, predicate) {
  return collection.reduce(function(all, item) {
    const last = all[all.length - 1];
    const same = last && predicate(last[0], item);
    if (same)
      last.push(item);
    else
      all.push([ item ]);
    return all;
  }, []);
}
